import {Drawer, Grid, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import AurButton from '../common/aur-button/AurButton';
import AurUserFormFields from '../user-management/aur-user-form-fields/AurUserFormFields';
import {FormProvider, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {
  bulkEditClientInfoValidationSchema,
  editTeamMemberFormValidationSchema,
} from '../pages/user-management/validation-schemas';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import AurPopup from '../common/aur-popup/AurPopup';
import {t} from 'i18next';
import AurIcon, {IconColors, IconNames} from '../common/aur-icon/AurIcon';
import {
  setBulkUserEdit,
  setDismissbanner,
  setDrawer,
  setErrors,
  setCurrentRow,
  setIsExistingUser,
  setProjectChange,
  adjustSummaryAfterErrorResolved,
} from '../../redux/slices/bulkUserEditSlice';
import {AurDismissBanner} from '../common/aur-banner/AurDismissBanner';
import AurSvgIcon from '../common/aur-svg-icon/AurSvgIcon';
import {
  IErrorResponses,
  IFormInput,
  IClientFormOption,
  UserErrorMsg,
} from './models';
import {bulkUploadEditUser} from '../../api/UserManagementService';
import {setUsers} from '../../redux/slices/bulkUserResultSlice';
import AurBulkClientFormFields from '../client-management/aur-client-form-fields/AurBulkClientFormFields';
import {
  AddClientInfoInputKey,
  TeamMemberInfoFormInputKey,
} from '../pages/user-management/models';
import {
  getPermissionGroupsAsLookup,
  getProjectsAsLookup,
  getRolesAsLookup,
  getWorkspacesAsLookup,
} from '../../api/ClientManagementService';
import {trimAfterDot} from '../../utils/bulkUserUploadUtlis';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import {PERMISSION_GROUP_CATEGORY} from '../../constants/constants';

/**
 * Defined the BulkUerEditState component
 * it will displays the Edit scrren for user details.
 * AurUserFormFields - for the user detail form
 * AurClientFormFields - for the user client form .
 */

const BulkUerEditState = () => {
  const dispatch = useDispatch();
  const {
    teamMemberInfo: teamMemberData,
    showDrawer,
    dismissBanner,
    currentUser,
    errors: errorData,
    projectChange,
    tabType,
  } = useSelector((state: RootState) => state.bulkUserEdit);
  const {users} = useSelector((state: RootState) => state.userResultScreen);
  const [teamMemberFormData, setTeamMemberFormData] = useState({
    ipAddressList: teamMemberData.ipAddressList,
    isLimitIp: teamMemberData.isLimitIp,
    ipAddressEditState: true,
    ipAddressError: false,
  });
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentUsers, setCurrentUsers] = useState<number>(
    currentUser.currentRow
  );
  const [userRoleOptions, setUserRoleOptions] = useState<IClientFormOption[]>(
    []
  );
  const [projectPermissionOptions, setProjectPermissionOptions] = useState<
    IClientFormOption[]
  >([]);
  const [clientPermissionOptions, setClientPermissionOptions] = useState<
    IClientFormOption[]
  >([]);
  const [projectOptions, setProjectOptions] = useState<IClientFormOption[]>([]);
  const [workspaceOptions, setWorkspaceOptions] = useState<IClientFormOption[]>(
    []
  );

  const [currentRowData, setCurrentRowData] = useState<any>();
  const [totalCount, setTotalCount] = useState<number>(currentUser.totalRow);

  useEffect(() => {
    const pageBaseIndex = (currentUser.page - 1) * currentUser.pageSize;
    const currentPageTotalRow = pageBaseIndex + users.length;

    setTotalCount(currentPageTotalRow);

    if (!users.length) {
      setCurrentRowData(undefined);
      return;
    }

    const boundedCurrentUser = Math.max(
      currentUser.startRow,
      Math.min(currentUsers, currentPageTotalRow)
    );

    if (boundedCurrentUser !== currentUsers) {
      setCurrentUsers(boundedCurrentUser);
      return;
    }

    const currentIndex = currentUsers - pageBaseIndex - 1;
    const safeIndex = Math.max(0, Math.min(users.length - 1, currentIndex));
    setCurrentRowData(users[safeIndex]);
  }, [
    currentUsers,
    currentUser.page,
    currentUser.pageSize,
    currentUser.startRow,
    users,
  ]);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).bulkUploadClientId;

  const combinedValidationSchema = editTeamMemberFormValidationSchema().concat(
    bulkEditClientInfoValidationSchema()
  );
  const methods = useForm({
    mode: 'all',
    defaultValues: {...teamMemberData, ipAddressList: []},
    resolver: yupResolver(combinedValidationSchema),
  });

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: {isValid, errors, isDirty},
    control,
    setValue,
    setError,
    clearErrors,
    reset,
  } = methods;

  useEffect(() => {
    if (currentRowData) {
      const ipAddress = currentRowData?.limitIpAddress
        ? currentRowData.limitIpAddress.split(',')
        : [];

      const isLimitIp = ipAddress.length > 0 && ipAddress[0] !== '';
      const dispatchData = {
        firstName: currentRowData.firstName ?? '',
        lastName: currentRowData.lastName ?? '',
        email: currentRowData.emailAddress ?? '',
        isBillable: currentRowData.billable,
        isLimitIp: isLimitIp,
        ipAddressList: ipAddress,
        role:
          currentRowData.userRole != null ? currentRowData.userRole.roleId : '',
        clientPermissionGroup:
          currentRowData.clientPermissionGroup?.length != 0
            ? (currentRowData.clientPermissionGroup?.[0]?.permissionGroupId ??
              '')
            : '',
        projectPermissionGroup:
          currentRowData.projectPermissionGroup?.length > 1
            ? (currentRowData.projectPermissionGroup?.[1]?.permissionGroupId ??
              '')
            : (currentRowData.projectPermissionGroup?.[0]?.permissionGroupId ??
              ''),
        project:
          currentRowData?.projects?.length != 0
            ? currentRowData.projects[0].projectId
            : '',
        isExistingUser: true,
        userImportTempId: currentRowData.userImportTempId,
        workspace: currentRowData?.projects[0]?.workspaces[0]?.workspaceId
          ? currentRowData?.projects[0]?.workspaces[0]?.workspaceId
          : '',
      };
      dispatch(setBulkUserEdit(dispatchData));
      setTeamMemberFormData({
        ipAddressList: ipAddress,
        isLimitIp: isLimitIp,
        ipAddressEditState: true,
        ipAddressError: false,
      });
      dispatch(setErrors(currentRowData.errors ?? []));
      dispatch(setIsExistingUser(true));
    }
    dispatch(setProjectChange(false));
  }, [currentRowData]);

  useEffect(() => {
    reset({
      ...teamMemberData,
      ipAddressList: [],
    });
    setTeamMemberFormData({
      ...teamMemberFormData,
      ipAddressList: teamMemberData.ipAddressList,
      ipAddressError: false,
      isLimitIp: teamMemberData.isLimitIp,
    });
  }, [reset, teamMemberData]);

  const isLimitIp = watch(TeamMemberInfoFormInputKey.isLimitIp);
  useEffect(() => {
    if (isLimitIp === false) {
      setValue(TeamMemberInfoFormInputKey.ipAddressList, []);
      setTeamMemberFormData({
        ...teamMemberFormData,
        ipAddressList: [],
        ipAddressError: false,
        isLimitIp: false,
      });
    } else {
      setTeamMemberFormData({...teamMemberFormData, isLimitIp: true});
    }
  }, [isLimitIp, trigger, reset]);
  const [isFetchRoleDataLoading, setIsFetchRoleDataLoading] = useState(false);
  const [isFetchPermissionDataLoading, setIsFetchPermissionDataLoading] =
    useState(false);
  const [isFetchProjectDataLoading, setIsFetchProjectDataLoading] =
    useState(false);
  const [isFetchWorkspaceLoading, setIsFetchWorkspaceLoading] = useState(false);

  useEffect(() => {
    const fetchRoleData = async () => {
      setIsFetchRoleDataLoading(true);
      try {
        const responseRole = await getRolesAsLookup();

        const filteredRoles = responseRole?.data?.filter(
          (item: any) =>
            item.name === 'Consilio User' || item.name === 'Client User'
        ); // removing the values from roles dropdown

        setUserRoleOptions(filteredRoles);
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetchRoleDataLoading(false);
      }
    };
    const fetchPermissionData = async () => {
      setIsFetchPermissionDataLoading(true);
      try {
        const responsePermission = await getPermissionGroupsAsLookup(
          clientId,
          PERMISSION_GROUP_CATEGORY.PROJECT
        );
        const clientPermission = await getPermissionGroupsAsLookup(
          clientId,
          PERMISSION_GROUP_CATEGORY.CLIENT
        );
        setProjectPermissionOptions(responsePermission?.data);
        setClientPermissionOptions(clientPermission?.data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetchPermissionDataLoading(false);
      }
    };
    const fetchProjectData = async () => {
      setIsFetchProjectDataLoading(true);
      try {
        const ODataQuery = '$filter=highlyConfidential eq false';
        const responseProjects = await getProjectsAsLookup(
          clientId,
          ODataQuery
        );
        setProjectOptions(responseProjects?.data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetchProjectDataLoading(false);
      }
    };
    fetchRoleData();
    fetchPermissionData();
    fetchProjectData();
  }, [clientId]);

  const projectValue = watch(AddClientInfoInputKey.projects);
  useEffect(() => {
    setWorkspaceOptions([]);
    if (projectChange) {
      setValue(AddClientInfoInputKey.workspace, '');
      trigger(AddClientInfoInputKey.workspace);
    }
    if (projectValue) {
      const fetchWorkspace = async () => {
        setIsFetchWorkspaceLoading(true);
        try {
          const responseWorkspaces = await getWorkspacesAsLookup(
            clientId,
            projectValue
          );
          setWorkspaceOptions(responseWorkspaces?.data);
        } catch (error) {
          console.error(error);
        } finally {
          setIsFetchWorkspaceLoading(false);
        }
      };

      fetchWorkspace();
    }
  }, [projectValue]);

  const mapErrorsToForm = (errorResponse: IErrorResponses) => {
    errorResponse?.forEach(({field, message}) => {
      let newMessage;
      if (field === 'limitIpAddress')
        setTeamMemberFormData({
          ...teamMemberFormData,
          ipAddressError: true,
          isLimitIp: true,
          ipAddressList: teamMemberData.ipAddressList,
        });
      if (field !== 'limitIpAddress') {
        if (field === 'billable') field = 'isBillable';
        if (field === 'userRole') field = 'role';
        if (field === 'emailAddress') {
          field = 'email';
          const firstMessage = message[0];
          newMessage = trimAfterDot(firstMessage);
        }

        setError(field as any, {
          type: 'manual',
          message: newMessage || message?.[0],
        });
      }
    });
  };

  // Call mapErrorsToForm when the component mounts or after an action
  useEffect(() => {
    if (errorData?.length != 0) mapErrorsToForm(errorData);
  }, [errorData, teamMemberData]);

  const onSubmit = async (submittedFormData: IFormInput) => {
    if (
      submittedFormData.project &&
      !submittedFormData.projectPermissionGroup
    ) {
      setError('projectPermissionGroup', {
        type: 'manual',
        message: t('validation.projPermissionGroupRequired'),
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        userInfo: {
          userImportTempId: submittedFormData.userImportTempId,
          firstName: submittedFormData.firstName,
          lastName: submittedFormData.lastName,
          emailAddress: submittedFormData.email,
          isBillable: submittedFormData.isBillable,
          ipAddressList: submittedFormData.ipAddressList,
        },
        clientInfo: {
          roleId: submittedFormData.role,
          projectPermissionGroupIds:
            submittedFormData.projectPermissionGroup !== ''
              ? [submittedFormData.projectPermissionGroup]
              : [],
          clientPermissionGroupIds: [submittedFormData.clientPermissionGroup],
          projects:
            submittedFormData.project !== ''
              ? [
                  {
                    projectId: submittedFormData.project,
                    workspaceIds:
                      submittedFormData.workspace !== ''
                        ? [submittedFormData.workspace]
                        : [],
                  },
                ]
              : [],
        },
      };
      const response = await bulkUploadEditUser(payload);
      const responseData = response?.data?.data;
      clearErrors();
      dispatch(setErrors([]));
      setTeamMemberFormData((prev) => ({
        ...prev,
        ipAddressError: false,
      }));
      const editedRowId =
        responseData?.userInfo?.userImportTempId ||
        submittedFormData.userImportTempId ||
        currentRowData?.userImportTempId;
      const pageBaseIndex = (currentUser.page - 1) * currentUser.pageSize;
      let currentUserIndex = currentUsers - pageBaseIndex - 1;
      if (currentUserIndex < 0 || currentUserIndex >= users.length) {
        currentUserIndex = users.findIndex(
          (user) => user.userImportTempId === editedRowId
        );
      }
      if (currentUserIndex < 0 && currentRowData?.userImportTempId) {
        currentUserIndex = users.findIndex(
          (user) => user.userImportTempId === currentRowData.userImportTempId
        );
      }

      let errorRowResolved = false;

      if (currentUserIndex >= 0) {
        const updatedUsers = [...users];
        const existingUser = updatedUsers[currentUserIndex];
        const existingUserData = existingUser as any;
        const selectedRole = userRoleOptions.find(
          (item) => item.id === responseData.clientInfo.roleId
        );
        const selectedClientPermissionGroup = clientPermissionOptions.find(
          (item) =>
            item.id ===
            (responseData.clientInfo.clientPermissionGroupIds?.[0] ?? '')
        );
        const selectedProjectPermissionGroup = projectPermissionOptions.find(
          (item) =>
            item.id ===
            (responseData.clientInfo.projectPermissionGroupIds?.[0] ?? '')
        );
        const selectedProject = projectOptions.find(
          (item) =>
            item.id === (responseData.clientInfo.projects?.[0]?.projectId ?? '')
        );
        const selectedWorkspace = workspaceOptions.find(
          (item) =>
            item.id ===
            (responseData.clientInfo.projects?.[0]?.workspaceIds?.[0] ?? '')
        );
        const updatedUser = {
          ...existingUser,
          firstName: responseData.userInfo.firstName,
          lastName: responseData.userInfo.lastName,
          emailAddress: responseData.userInfo.emailAddress,
          billable: responseData.userInfo.isBillable,
          limitIpAddress: responseData.userInfo.ipAddressList?.join(',') ?? '',
          role: existingUserData?.role
            ? {...existingUserData.role, roleId: responseData.clientInfo.roleId}
            : existingUserData?.role,
          userRole: existingUserData?.userRole
            ? {
                ...existingUserData.userRole,
                roleId: responseData.clientInfo.roleId,
                name: selectedRole?.name ?? existingUserData.userRole.name,
              }
            : {
                roleId: responseData.clientInfo.roleId,
                name: selectedRole?.name ?? '',
                roleKey: existingUserData?.userRole?.roleKey ?? '',
              },
          clientPermissionGroup: [
            {
              permissionGroupId:
                responseData.clientInfo.clientPermissionGroupIds?.[0] ?? '',
              permissionGroupName:
                selectedClientPermissionGroup?.name ??
                existingUserData?.clientPermissionGroup?.[0]
                  ?.permissionGroupName ??
                '',
            },
          ],

          projectPermissionGroup: [
            {
              permissionGroupId:
                responseData.clientInfo.projectPermissionGroupIds?.[0] ?? '',
              permissionGroupName:
                selectedProjectPermissionGroup?.name ??
                existingUserData?.projectPermissionGroup?.[0]
                  ?.permissionGroupName ??
                '',
            },
          ],
          projects:
            existingUserData?.projects?.length > 0
              ? [
                  {
                    ...existingUserData.projects[0],
                    projectId:
                      responseData.clientInfo.projects?.[0]?.projectId ?? '',
                    projectName:
                      selectedProject?.name ??
                      existingUserData.projects[0].projectName,
                    workspaces:
                      existingUserData.projects[0].workspaces?.length > 0
                        ? [
                            {
                              ...existingUserData.projects[0].workspaces[0],
                              workspaceId:
                                responseData.clientInfo.projects?.[0]
                                  ?.workspaceIds?.[0] ?? '',
                              workspaceName: responseData.clientInfo
                                .projects?.[0]?.workspaceIds?.[0]
                                ? (selectedWorkspace?.name ??
                                  existingUserData.projects[0].workspaces[0]
                                    .workspaceName)
                                : '',
                            },
                          ]
                        : [
                            {
                              workspaceId:
                                responseData.clientInfo.projects?.[0]
                                  ?.workspaceIds?.[0] ?? '',
                              workspaceName: selectedWorkspace?.name ?? '',
                            },
                          ],
                  },
                ]
              : [
                  {
                    projectId:
                      responseData.clientInfo.projects?.[0]?.projectId ?? '',
                    projectName: selectedProject?.name ?? '',
                    workspaces: [
                      {
                        workspaceId:
                          responseData.clientInfo.projects?.[0]
                            ?.workspaceIds?.[0] ?? '',
                        workspaceName: selectedWorkspace?.name ?? '',
                      },
                    ],
                  },
                ],
          isError: false,
          errors: [],
        };
        const wasErrorRow =
          existingUserData?.isError === true ||
          (Array.isArray(existingUserData?.errors) &&
            existingUserData.errors.length > 0) ||
          (Array.isArray(errorData) && errorData.length > 0);

        if (wasErrorRow) {
          errorRowResolved = true;
          const rowErrors = (
            Array.isArray(existingUserData?.errors) &&
            existingUserData.errors.length > 0
              ? existingUserData.errors
              : errorData
          ) as UserErrorMsg[];
          const resolvedErrorCount =
            rowErrors?.reduce(
              (total: number, error: UserErrorMsg) =>
                total + error.message.length,
              0
            ) ?? 0;

          dispatch(adjustSummaryAfterErrorResolved({resolvedErrorCount}));
        }

        if (tabType === 'Errors' && wasErrorRow) {
          updatedUsers.splice(currentUserIndex, 1);
          const pageBaseIndex = (currentUser.page - 1) * currentUser.pageSize;
          const nextTotalRow = pageBaseIndex + updatedUsers.length;
          const nextCurrentRow =
            updatedUsers.length > 0
              ? Math.min(currentUsers, nextTotalRow)
              : currentUser.startRow;

          setTotalCount(nextTotalRow);
          setCurrentUsers(nextCurrentRow);
          dispatch(
            setCurrentRow({
              currentRow: nextCurrentRow,
              totalRow: nextTotalRow,
              startRow: currentUser.startRow,
              page: currentUser.page,
              pageSize: currentUser.pageSize,
            })
          );

          if (updatedUsers.length === 0) {
            dispatch(setDrawer(false));
          }
          dispatch(setUsers(updatedUsers));
        } else {
          updatedUsers[currentUserIndex] = updatedUser as any;
          dispatch(setUsers(updatedUsers));
        }
      }

      if (users.length === 1 && tabType === 'Errors' && !errorRowResolved) {
        const resultData = {
          firstName: responseData.userInfo.firstName,
          lastName: responseData.userInfo.lastName,
          email: responseData.userInfo.emailAddress,
          isBillable: responseData.userInfo.isBillable,
          userImportTempId: responseData.userInfo.userImportTempId,
          isLimitIp: responseData.userInfo.ipAddressList.length !== 0,
          ipAddressList: [],
          role: responseData.clientInfo.roleId,
          clientPermissionGroup:
            responseData.clientInfo.clientPermissionGroupIds?.[0] ?? '',
          projectPermissionGroup:
            responseData.clientInfo.projectPermissionGroupIds?.[0] ?? '',
          project: responseData.clientInfo.projects?.[0]?.projectId ?? '',
          workspace:
            responseData.clientInfo.projects?.[0]?.workspaceIds?.[0] ?? '',
        };
        reset({...resultData, ipAddressList: []});
        setTeamMemberFormData({
          ...teamMemberFormData,
          ipAddressList: responseData.userInfo.ipAddressList,
          ipAddressError: false,
          isLimitIp: resultData.isLimitIp,
        });
      }
    } catch (error: any) {
      if (error.status === 400) {
        const validationError = error.response.data.validationErrors;
        mapErrorsToForm(validationError);
      }
    } finally {
      setIsSaving(false);
    }
  };
  // Function for closing the drawer
  const drawerClose = () => {
    if (isDirty) setCancelModal(true);
    else {
      dispatch(setProjectChange(false));
      dispatch(setDrawer(false));
    }
  };
  // Function for click on don't save button
  const editClose = () => {
    if (isDirty) setCancelModal(true);
    else {
      dispatch(setDrawer(false));
      dispatch(setProjectChange(false));
    }
  };

  const navigateToBulkUploadScreen = () => {
    setCancelModal(false);
    dispatch(setDrawer(false));
    dispatch(setProjectChange(false));
  };
  // Function to handle cancellation of the upload
  const closeModalHandler = () => {
    setCancelModal(false);
    dispatch(setDrawer(true));
  };

  // Function to generate previous users data in form
  const navigateLeft = () => {
    setCurrentUsers((prev: number) => prev - 1);
  };
  // Function to generate next users data in form
  const navigateRight = () => {
    setCurrentUsers((prev: number) => prev + 1);
  };

  const handleDismiss = () => {
    dispatch(setDismissbanner(false));
  };
  return (
    <Drawer open={showDrawer} anchor="right">
      <Grid className="drawer-container">
        <Grid className="drawer-header">
          <AurButton
            type="exit"
            label={t('buttons.close')}
            title={t('buttons.close')}
            size="Medium"
            onClick={drawerClose}
            dataTestId={'closeBtn-test'}
          />
          {dismissBanner && (
            <Typography className="fix-errorText">
              {t('common.editDetails.fixErrors')}
            </Typography>
          )}
          <div className="drawer-header-pagination">
            <AurButton
              type={'exit'}
              size="Medium"
              onClick={navigateLeft}
              disabled={currentUsers === currentUser.startRow}
              dataTestId="buttonChevronLeft-test"
              classNames="edit-header-button"
              title={t('common.bulkUploadModule.previous')}
            >
              {' '}
              <AurIcon
                icon={IconNames.ChevronLeft}
                color={
                  currentUsers === currentUser.startRow
                    ? IconColors.Secondary
                    : IconColors.Primary
                }
              />{' '}
            </AurButton>
            <Typography className="drawer-header-paginationText">
              {currentUsers}{' '}
              <span className="mist-color-500">{`of ${totalCount}`}</span>
            </Typography>
            <AurButton
              type={'exit'}
              size="Medium"
              onClick={navigateRight}
              disabled={currentUsers === totalCount}
              dataTestId="buttonChevronRight-test"
              classNames="edit-header-button"
              title={t('common.bulkUploadModule.next')}
            >
              <AurIcon
                icon={IconNames.ChevronRight}
                color={
                  currentUsers === totalCount
                    ? IconColors.Secondary
                    : IconColors.Primary
                }
              />
            </AurButton>
          </div>
        </Grid>
        {dismissBanner && (
          <AurDismissBanner
            className="aur-dismiss-banner-edit"
            containerClassName="aur-dismiss-edit-container"
            bannerHeading={t('common.dismissBanner.editTitle')}
            bannerSummary={t('common.dismissBanner.editSummary')}
            icon={<AurSvgIcon src="images/edit-banner.svg" />}
            aria-live="assertive"
          >
            <AurButton
              classNames="dismiss-button"
              type={'exit'}
              label={t('buttons.dismiss')}
              onClick={handleDismiss}
              aria-label={t('common.dismissBanner.dismissBannerBtnAriaLabel')}
              size="Medium"
            />
          </AurDismissBanner>
        )}

        <FormProvider {...methods}>
          <form
            autoComplete="off"
            data-testid="teamMemberForm__form"
            className="team-member-form"
            onSubmit={(..._arguments) =>
              void handleSubmit(onSubmit)(..._arguments)
            }
          >
            <Grid className="edit-form-container">
              <Grid className="user-container">
                <Grid className="edit-user-title ">
                  <Typography className="typo-text headline2_size mist-color-100">
                    {t('common.editDetails.editTeamMember')}
                  </Typography>
                  <div className="edit-user-buttons">
                    <AurButton
                      type={'exit'}
                      label={t('buttons.dontSaveUpdate')}
                      title={t('buttons.dontSaveUpdate')}
                      disabled={!isDirty}
                      size="Medium"
                      onClick={editClose}
                      dataTestId="dontSaveButton-test"
                      classNames="edit-header-button"
                    />

                    <AurButton
                      type={'continue'}
                      label={t('buttons.saveChanges')}
                      title={t('buttons.saveChanges')}
                      disabled={
                        !isDirty || !isValid || Object.keys(errors).length > 0
                      }
                      size="Medium"
                      buttonType="submit"
                      dataTestId="saveButton-test"
                      classNames="edit-header-button"
                    />
                  </div>
                </Grid>
                <Grid className="edit-user-fields">
                  <AurUserFormFields
                    control={control}
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    isValid={isValid}
                    buttonVisible={false}
                    leftGridSize={6}
                    rightGridSize={6}
                    activeVisible={false}
                    teamMemberData={teamMemberFormData}
                    isBulkUserEdit
                  />
                </Grid>
              </Grid>

              <Grid className="client-container">
                <Grid className="client-header">
                  {' '}
                  <Typography className="typo-text body3_size color-mist-900">
                    {t('common.editDetails.clientList')}
                  </Typography>
                </Grid>
                <Grid className=" client-content ">
                  <Typography className="typo-text headline2_size mist-color-100 client-title">
                    {t('common.editDetails.editClient')}
                  </Typography>
                  <AurBulkClientFormFields
                    control={control}
                    roleOptionList={userRoleOptions}
                    projectPermissionOptionList={projectPermissionOptions}
                    clientPermissionOptionList={clientPermissionOptions}
                    workspaceOptionList={workspaceOptions}
                    projectOptions={projectOptions}
                    isValid={isValid}
                    buttonLabel={t('addEditTeamMember.form.saveAndContine')}
                    clientVisible={false}
                    buttonVisible={false}
                    fieldSize={8}
                    projectMaxLength={42}
                    permissionMaxLength={40}
                  />
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Grid>
      {cancelModal && (
        <AurPopup
          aria-label={t('common.cancelUploadPopup.ariaLable')}
          aria-description={t('common.cancelUploadPopup.ariaDescription')}
          className="inactivity-popup-modal"
          open={cancelModal}
          onOk={navigateToBulkUploadScreen}
          onCancel={closeModalHandler}
          popupTitle={t(`common.cancelUploadPopup.title`)}
          popupHeading={t(`common.cancelUploadPopup.heading`)}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={false}
          popupContent={t(`common.cancelUploadPopup.content`)}
          labelActionCancel={t(`common.cancelUploadPopup.buttonStay`)}
          labelActionOk={t(`common.cancelUploadPopup.buttonYes`)}
        />
      )}
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={
            isSaving ||
            isFetchRoleDataLoading ||
            isFetchPermissionDataLoading ||
            isFetchProjectDataLoading ||
            isFetchWorkspaceLoading
          }
        />
      }
    </Drawer>
  );
};

export default BulkUerEditState;
